/* @flow */

import { normalizeUser, normalizeUsers } from '@e180/domain/lib/user/util';
import { topicGroupKind } from '@e180/domain/lib/topic/setting';

import * as namespaces from './userActionNamespaces';
import * as types from './userActionTypes';
import { MARKET_AUTOCOMPLETE_USERS } from 'src/shared/app/market/action/marketActionNamespaces';
import { patchEntities } from 'src/shared/domain/base/action/entityActions';
import {
  userActivityEndpoint,
  userEndpoint,
  userStatisticsEndpoint,
  userCanCreateTopicEndpoint,
  usersEndpoint,
  userAvatarEndpoint,
  userTopicKeywordsEndpoint,
  userInterestsEndpoint,
} from 'src/shared/app/base/settings/endpointSettings';
import { parseResponse } from 'src/shared/app/base/util/ajaxUtils';

import type { Keyword } from 'src/shared/domain/keyword/type/keywordTypes';
import type { UserActivity } from '@e180/domain/lib/user/type';

// All users

export function fetchUsers(
  eventId: number,
  params?: Object,
  namespace?: string,
) {
  return (dispatch: any, { get }: Object) => {
    dispatch(fetchUsersRequest(namespace));

    return get(usersEndpoint(eventId), params)
      .then(parseResponse)
      .then((json) => {
        const normalizedData = normalizeUsers(json.results);
        const { entities, result } = normalizedData;

        dispatch(patchEntities(entities));
        // $FlowIssue
        dispatch(fetchUsersSuccess(result, namespace));
      })
      .catch((e) => {
        dispatch(fetchUsersFailure(e, namespace));

        throw e;
      });
  };
}

export function fetchUsersRequest(namespace?: string) {
  return {
    type: types.FETCH_USERS_REQUEST,
    namespace,
  };
}

export function fetchUsersSuccess(result: Array<number>, namespace?: string) {
  return {
    type: types.FETCH_USERS_SUCCESS,
    result,
    namespace,
  };
}

export function fetchUsersFailure(exception: Error, namespace?: string) {
  return {
    type: types.FETCH_USERS_FAILURE,
    exception,
    namespace,
  };
}

// Single user

export function fetchUser(
  id: number | 'me',
  eventId?: number,
  options?: Object,
  namespace?: string,
) {
  return (dispatch: any, { get }: Object) => {
    dispatch(fetchUserRequest(namespace));

    const params = {};

    if (options && typeof options === 'object' && options.optimized) {
      params.light = true;
    }

    return get(userEndpoint(id, eventId), params)
      .then(parseResponse)
      .then((json) => {
        const normalizedData = normalizeUser(json);
        const { entities, result } = normalizedData;

        dispatch(patchEntities(entities));
        // $FlowIssue
        dispatch(fetchUserSuccess(result, namespace));

        return json;
      })
      .catch((e) => {
        dispatch(fetchUserFailure(e, namespace));

        throw e;
      });
  };
}

export function fetchUserRequest(namespace?: string) {
  return {
    type: types.FETCH_USER_REQUEST,
    namespace,
  };
}

export function fetchUserSuccess(result: number, namespace?: string) {
  return {
    type: types.FETCH_USER_SUCCESS,
    result,
    namespace,
  };
}

export function fetchUserFailure(exception: Error, namespace?: string) {
  return {
    type: types.FETCH_USER_FAILURE,
    exception,
    namespace,
  };
}

// Reset

export function resetUser() {
  return {
    type: types.RESET_USER,
  };
}

// Fetch user statistics

export function fetchUserStatistics(id: number, eventId: number) {
  return (dispatch: any, { smartGet }: Object) => {
    dispatch(fetchUserStatisticsRequest());

    return smartGet(userStatisticsEndpoint(id, eventId))
      .then((result) => {
        dispatch(fetchUserStatisticsSuccess(id, result));

        return result;
      })
      .catch((exception) => {
        dispatch(fetchUserStatisticsFailure(exception));

        throw exception;
      });
  };
}

export function fetchUserStatisticsRequest() {
  return {
    type: types.FETCH_USER_STATISTICS_REQUEST,
  };
}

export function fetchUserStatisticsSuccess(userId: number, result: number) {
  return {
    type: types.FETCH_USER_STATISTICS_SUCCESS,
    userId,
    result,
  };
}

export function fetchUserStatisticsFailure(exception: Error) {
  return {
    type: types.FETCH_USER_STATISTICS_FAILURE,
    exception,
  };
}

// Fetch user ability to create a new topic

export function fetchUserCanCreateGroupTopic(id: number, eventId: number) {
  return (dispatch: any, { smartGet }: Object) => {
    dispatch(fetchUserCanCreateGroupTopicRequest());

    return smartGet(userCanCreateTopicEndpoint(id, eventId, topicGroupKind))
      .then((result) => {
        dispatch(fetchUserCanCreateGroupTopicSuccess(result));

        return result;
      })
      .catch((exception) => {
        dispatch(fetchUserCanCreateGroupTopicFailure(exception));
        throw exception;
      });
  };
}

export function fetchUserCanCreateGroupTopicRequest() {
  return {
    type: types.FETCH_USER_CAN_CREATE_GROUP_TOPIC_REQUEST,
  };
}

export function fetchUserCanCreateGroupTopicSuccess(result: Object) {
  return {
    type: types.FETCH_USER_CAN_CREATE_GROUP_TOPIC_SUCCESS,
    result,
  };
}

export function fetchUserCanCreateGroupTopicFailure(exception: Error) {
  return {
    type: types.FETCH_USER_CAN_CREATE_GROUP_TOPIC_FAILURE,
    exception,
  };
}

// Patch user

export function patchUser(userId: number | string, data: Object) {
  return (dispatch: any, { patch }: Object) => {
    dispatch(patchUserRequest());

    return patch(userEndpoint(userId), data)
      .then(parseResponse)
      .then((json) => {
        const normalizedData = normalizeUser(json);
        const { entities, result } = normalizedData;

        dispatch(patchEntities(entities));
        // $FlowIssue
        dispatch(patchUserSuccess(result));

        return result;
      })
      .catch((e) => {
        dispatch(patchUserFailure(e));

        throw e;
      });
  };
}

export function patchUserRequest() {
  return {
    type: types.PATCH_USER_REQUEST,
  };
}

export function patchUserSuccess(result: number) {
  return {
    type: types.PATCH_USER_SUCCESS,
    result,
  };
}

export function patchUserSignupSuccess() {
  return {
    type: types.PATCH_USER_SIGNUP_SUCCESS,
  };
}

export function patchUserFailure(exception: Error) {
  return {
    type: types.PATCH_USER_FAILURE,
    exception,
  };
}

// Self

export function fetchSelf(options?: Object) {
  return fetchUser('me', undefined, options, namespaces.SELF);
}

export function fetchSelfOptimally() {
  return fetchSelf({ optimized: true });
}

export function patchSelf(data: Object) {
  return patchUser('me', data);
}

// Activity

export function fetchUserActivity(
  eventId: number,
  id: number,
  onlyConfirmed?: boolean = false,
  ignoreOwnBraindates?: boolean = false,
  ignoreBraindateId?: number,
) {
  return (dispatch: any, { get }: Object) => {
    dispatch(fetchUserActivityRequest());

    const data = {
      event: eventId,
    };

    if (onlyConfirmed) {
      // $FlowIssue
      data.confirmed = true;
    }

    if (ignoreOwnBraindates) {
      // $FlowIssue
      data['ignore-my-braindates'] = ignoreOwnBraindates;
    }

    if (ignoreBraindateId) {
      // $FlowIssue
      data['ignore-braindate-id'] = ignoreBraindateId;
    }

    return get(userActivityEndpoint(id), data)
      .then(parseResponse)
      .then((json) => dispatch(fetchUserActivitySuccess(json, id)))
      .catch((e) => {
        dispatch(fetchUserActivityFailure(e));

        throw e;
      });
  };
}

export function fetchUserActivityRequest() {
  return {
    type: types.FETCH_USER_ACTIVITY_REQUEST,
  };
}

export function fetchUserActivitySuccess(
  result: Array<UserActivity>,
  userId: number,
) {
  return {
    type: types.FETCH_USER_ACTIVITY_SUCCESS,
    result,
    userId,
  };
}

export function fetchUserActivityFailure(exception: Error) {
  return {
    type: types.FETCH_USER_ACTIVITY_FAILURE,
    exception,
  };
}

// Autocomplete

export function fetchBraindatersAutocomplete(eventId: number, query?: string) {
  const params = {
    topics_count: 1,
    q: query,
  };

  return fetchUsers(eventId, params, MARKET_AUTOCOMPLETE_USERS);
}

// Avatar

export function updateUserAvatar(
  id: number,
  file: any,
  cropCenter: Object,
  cropDim: Object,
) {
  const { x, y } = cropCenter;
  const { width, height } = cropDim;

  return (dispatch: any, { post }: Object) => {
    dispatch(updateUserAvatarRequest());

    const data = new FormData();

    data.append('image', file);
    data.append('center_x', x);
    data.append('center_y', y);
    data.append('width', width);
    data.append('height', height);

    return post(userAvatarEndpoint(id), data)
      .then(parseResponse)
      .then((avatar) => dispatch(updateUserAvatarSuccess(avatar, id)))
      .catch((e) => {
        dispatch(updateUserAvatarFailure(e));

        throw e;
      });
  };
}

export function updateUserAvatarRequest() {
  return {
    type: types.UPDATE_AVATAR_REQUEST,
  };
}

export function updateUserAvatarSuccess(result: Object, userId: number) {
  return {
    type: types.UPDATE_AVATAR_SUCCESS,
    result,
    userId,
  };
}

export function updateUserAvatarFailure(exception: Error) {
  return {
    type: types.UPDATE_AVATAR_FAILURE,
    exception,
  };
}

/*
 |------------------------------------------------------------------------------
 | Topic Keywords
 |------------------------------------------------------------------------------
 */

export function simulateUserTopicKeywordsRequest(userId: number) {
  return {
    type: types.SIMULATE_USER_TOPIC_KEYWORDS_REQUEST,
    userId,
  };
}

export function fetchUserTopicKeywords(userId: number, eventId: number) {
  return (dispatch: any, { get }: Object) => {
    dispatch(fetchUserTopicKeywordsRequest(userId));

    return get(userTopicKeywordsEndpoint(userId, eventId))
      .then(parseResponse)
      .then((json) => dispatch(fetchUserTopicKeywordsSuccess(userId, json)))
      .catch((e) => {
        dispatch(fetchUserTopicKeywordsFailure(userId, e));

        throw e;
      });
  };
}

export function fetchUserTopicKeywordsRequest(userId: number) {
  return {
    type: types.FETCH_USER_TOPIC_KEYWORDS_REQUEST,
    userId,
  };
}

export function fetchUserTopicKeywordsSuccess(
  userId: number,
  result: Array<Keyword>,
) {
  return {
    type: types.FETCH_USER_TOPIC_KEYWORDS_SUCCESS,
    userId,
    result,
  };
}

export function fetchUserTopicKeywordsFailure(
  userId: number,
  exception: Error,
) {
  return {
    type: types.FETCH_USER_TOPIC_KEYWORDS_FAILURE,
    userId,
    exception,
  };
}

/*
 |------------------------------------------------------------------------------
 | Interests
 |------------------------------------------------------------------------------
 */

export function simulateUserInterestsRequest(userId: number) {
  return {
    type: types.SIMULATE_USER_INTERESTS_REQUEST,
    userId,
  };
}

export function fetchUserInterests(userId: number, eventId: number) {
  return (dispatch: any, { get }: Object) => {
    dispatch(fetchUserInterestsRequest(userId));

    return get(userInterestsEndpoint(userId, eventId))
      .then(parseResponse)
      .then((json) => dispatch(fetchUserInterestsSuccess(userId, json)))
      .catch((e) => {
        dispatch(fetchUserInterestsFailure(userId, e));

        throw e;
      });
  };
}

export function fetchUserInterestsRequest(userId: number) {
  return {
    type: types.FETCH_USER_INTERESTS_REQUEST,
    userId,
  };
}

export function fetchUserInterestsSuccess(
  userId: number,
  result: Array<Keyword>,
) {
  return {
    type: types.FETCH_USER_INTERESTS_SUCCESS,
    userId,
    result,
  };
}

export function fetchUserInterestsFailure(userId: number, exception: Error) {
  return {
    type: types.FETCH_USER_INTERESTS_FAILURE,
    userId,
    exception,
  };
}
